import api from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/constants';

export interface TopItemReport {
  item_id: number;
  item_sku: string;
  item_name: string;
  category: string;
  qty_sold: number;
  total_revenue: number;
  total_profit: number;
}

export interface TopCustomerReport {
  customer_id?: number;
  customer_name: string;
  customer_phone?: string;
  total_transactions: number;
  total_spent: number;
  total_items_purchased: number;
}

export interface PaymentBreakdown {
  cash: number;
  transfer: number;
  qris: number;
  edc: number;
}

export interface DailySalesReport {
  date: string;
  branch_id?: number;
  branch_name?: string;
  total_transactions: number;
  total_items_sold: number;
  gross_sales: number;
  total_discount: number;
  net_sales: number;
  total_cost: number;
  gross_profit: number;
  profit_margin: number;
  payment_breakdown: PaymentBreakdown;
  top_items: TopItemReport[];
  void_count: number;
  void_amount: number;
}

export interface MonthlySalesReport {
  year: number;
  month: number;
  month_name: string;
  branch_id?: number;
  branch_name?: string;
  total_transactions: number;
  total_items_sold: number;
  avg_transactions_per_day: number;
  gross_sales: number;
  total_discount: number;
  net_sales: number;
  total_cost: number;
  gross_profit: number;
  profit_margin: number;
  avg_daily_sales: number;
  best_day_date?: string;
  best_day_sales: number;
  worst_day_date?: string;
  worst_day_sales: number;
  payment_breakdown: PaymentBreakdown;
  top_items: TopItemReport[];
  top_customers: TopCustomerReport[];
  previous_month_sales?: number;
  growth_amount?: number;
  growth_percentage?: number;
}

const API_URL = `${API_BASE_URL}/reports`;

export const getDailyReport = async (params?: {
  target_date?: string;
  branch_id?: number;
}): Promise<DailySalesReport> => {
  const response = await api.get<DailySalesReport>(
    `${API_URL}/daily`,
    { params }
  );
  return response.data;
};

export const getMonthlyReport = async (params?: {
  year?: number;
  month?: number;
  branch_id?: number;
}): Promise<MonthlySalesReport> => {
  const response = await api.get<MonthlySalesReport>(
    `${API_URL}/monthly`,
    { params }
  );
  return response.data;
};

export const exportDailyReportPDF = async (params?: {
  target_date?: string;
  branch_id?: number;
}): Promise<Blob> => {
  const response = await api.get(`${API_URL}/daily/export/pdf`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const exportDailyReportExcel = async (params?: {
  target_date?: string;
  branch_id?: number;
}): Promise<Blob> => {
  const response = await api.get(`${API_URL}/daily/export/excel`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const exportMonthlyReportPDF = async (params?: {
  year?: number;
  month?: number;
  branch_id?: number;
}): Promise<Blob> => {
  const response = await api.get(`${API_URL}/monthly/export/pdf`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const exportMonthlyReportExcel = async (params?: {
  year?: number;
  month?: number;
  branch_id?: number;
}): Promise<Blob> => {
  const response = await api.get(`${API_URL}/monthly/export/excel`, {
    params,
    responseType: 'blob',
  });
  return response.data;
};

// Aliases for backward compatibility
export const getSalesReport = getDailyReport;
export const getStockReport = async (params?: { branch_id?: number }) => {
  // TODO: Implement stock report endpoint when backend is ready
  return [];
};
